//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { PoolStateToString, yupErc20WeiRequired, yupEthAddrRequired } from './Enums'
import { PoolBaseOverviewCTO, PoolBaseOverviewCTOSchema } from './PoolBaseCTO'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { ZeroAddress } from 'ethers'

export const PoolFlexOverviewCTOSchema = yup
  .object({
    businessDayRegistryAddr: yupEthAddrRequired
      .label('Business Day Registry Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'businessDayRegistryAddr',
          description: 'The smart-contract address of the business day registry. ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    closeOfDepositTime: yup
      .number()
      .required()
      .default(0)
      .label('Advance Cut-Off Time')
      .meta(
        toFieldMetaData({
          propName: 'closeOfDepositTime',
          description:
            'The cut-off time for loans in seconds from 0:00 UTC e.g. 64800 is 18:00 UTC. Any loans received after this time will be treated as if they were received the next business day.',
          stringType: StringTypeEnum.ETH_TIME_OF_DAY,
        })
      ),
    closeOfWithdrawTime: yup
      .number()
      .required()
      .label('Withdrawal Request Cut-Off Time')
      .default(0)
      .meta(
        toFieldMetaData({
          propName: 'closeOfWithdrawTime',
          description:
            'The cut-off time for withdrawal request in seconds from 0:00 UTC e.g. 64800 is 18:00 UTC. Any withdrawal requests received after this time will be treated as if they were received the next business day. ',
          stringType: StringTypeEnum.ETH_TIME_OF_DAY,
        })
      ),

    transferInDays: yup
      .number()
      .required()
      .default(0)
      .label('Advance Processing Time (Days)')
      .meta({
        defaultDisplayValue: '0',
        ...toFieldMetaData({
          propName: 'transferInDays',
          description: 'The number of business days that will lapse before a loan starts accruing interest. ',
        }),
      }),
    transferOutDays: yup
      .number()
      .required()
      .default(0)
      .label('Withdrawal Processing Time (Days)')
      .meta({
        defaultDisplayValue: '0',
        ...toFieldMetaData({
          propName: 'transferOutDays',
          description: 'The number of business days that will lapse before a loan is due to be repaid. ',
        }),
      }),

    nonBusinessDays: yup
      .array()
      .of(yup.number().required())
      .label('Non-Business Days')
      .required()
      .default([])
      .meta(
        toFieldMetaData({
          propName: 'nonBusinessDays',
          description:
            'A list of all non-weekend, non-business days. Includes any day Mon-Fri which is a banking holiday in the US and UK. ',
        })
      ),

    feeVaultAddr: yupEthAddrRequired
      .label('Fee Vault Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'feeVaultAddr',
          stringType: StringTypeEnum.ETH_ADDR,
          description: 'The smart-contract address for the Fee Vault. ',
        })
      ),
    withdrawControllerAddr: yupEthAddrRequired
      .label('Withdraw Controller Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'withdrawControllerAddr',
          description: 'The smart-contract address for the Withdrawal Controller',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    borrowerVaultAddr: yupEthAddrRequired
      .label('Borrower Vault Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'borrowerVaultAddr',
          description: 'The smart-contract address of the Borrower Vault',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    maxCapacity: yupErc20WeiRequired
      .label('Max Vault Capacity')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'maxCapacity',
          description:
            'The maximum outstanding loan principal for the loan vault. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    interestRate: yupErc20WeiRequired
      .label('Loan Fee Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'interestRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 10,
          description:
            'The current annualised interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
          stringType: StringTypeEnum.APR,
        })
      ),

    dailyInterestRate: yupErc20WeiRequired
      .label('Daily Loan Fee Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'dailyInterestRate',
          description:
            'The daily interest rate to be accrued on principal earning interest. This is calculated as (1 + interestRate) ^ 1/365 - 1. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),

    state: yup
      .number()
      .label('State')
      .required()
      .default(0)
      .meta(
        toFieldMetaData({
          propName: 'state',
          description: 'The State of the Loan Vault',
          stateToString: PoolStateToString,
        })
      ),

    totalPrincipalEarningInterest: yupErc20WeiRequired
      .label('Total Principal Earning Interest')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalPrincipalEarningInterest',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 11,
          description:
            'The total amount of principal currently earning the Loan Fee Rate. Principal and interest compound daily. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalInterestAccrued: yupErc20WeiRequired
      .label('Total Interest Accrued')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalInterestAccrued',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 12,
          description:
            'The total amount of interest accrued to date. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    totalAssetsDeposited: yupErc20WeiRequired
      .label('Total Assets Loaned')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsDeposited',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 13,
          description:
            'The total amount of assets lent since vault inception. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    totalAssetsWithdrawn: yupErc20WeiRequired
      .label('Total Assets Withdrawn')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsWithdrawn',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 14,
          description:
            'The total amount of assets withdrawn since vault inception. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    exchangeRate: yupErc20WeiRequired
      .label('Exchange Rate')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'exchangeRate',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 15,
          description:
            'The current exchange rate at which Vault Tokens and Assets will be exchanged. The Exchange Rate increases by the Daily Interest Rate each day. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),
    totalSupply: yupErc20WeiRequired
      .label('Total Vault Tokens Outstanding')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalSupply',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 16,
          description:
            'The total number of vault tokens outstanding. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    totalRequestedShares: yupErc20WeiRequired
      .label('Total Outstanding Withdrawal Requests (Tokens)')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalRequestedShares',
          description:
            'The total amount of outstanding withdrawal requests, denominated in vault tokens. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    totalRequestedAssets: yupErc20WeiRequired
      .label('Total Outstanding Withdrawal Requests (Assets)')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalRequestedAssets',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 17,
          description:
            'The total amount of outstanding withdrawal requests, denominated in assets. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    totalSharesTransitioningIn: yupErc20WeiRequired
      .label('Total Loans Pending (Tokens)')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalSharesTransitioningIn',
          description:
            'The total amount of loans currently pending, denominated in vault tokens. The tokens will begin accruing interest after the transferInDays lapse. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00 ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),

    totalAssetsTransitioningIn: yupErc20WeiRequired
      .label('Total Loans Pending (Assets)')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsTransitioningIn',
          description:
            'The total amount of loans currently pending, denominated in assets. The assets will begin accruing interest after the transferInDays lapse. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalAssetsDueForWithdraws: yupErc20WeiRequired
      .label('Total Withdrawal Request Due')
      .required()
      .default('0')
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsDueForWithdraws',
          columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
          columnOrder: 18,
          description:
            'The total amount of assets which are due to be repaid. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    lastDayAccrued: yup
      .number()
      .default(0)
      .label('lastDayAccrued')
      .required()
      .meta(toFieldMetaData({ propName: 'lastDayAccrued', description: ' lastDayAccrued ' })),
  })
  .concat(PoolBaseOverviewCTOSchema)
  .label('PoolFlexOverviewCTO')
  .meta(toFieldMetaData({ propName: 'PoolFlexOverviewCTO', description: 'PoolFlexOverviewCTOSchema' }))

export interface PoolFlexOverviewCTO extends PoolBaseOverviewCTO {
  interestRate: ERC20Wei
  dailyInterestRate: ERC20Wei
  state: number
  totalPrincipalEarningInterest: ERC20Wei
  totalInterestAccrued: ERC20Wei
  totalAssetsDeposited: ERC20Wei
  totalAssetsWithdrawn: ERC20Wei
  exchangeRate: ERC20Wei
  totalSupply: ERC20Wei
  totalRequestedShares: ERC20Wei
  totalRequestedAssets: ERC20Wei
  totalAssetsTransitioningIn: ERC20Wei
  totalSharesTransitioningIn: ERC20Wei
  totalAssetsDueForWithdraws: ERC20Wei
  lastDayAccrued: number

  businessDayRegistryAddr: EthAddr
  nonBusinessDays: number[]

  closeOfWithdrawTime: number

  closeOfDepositTime: number
  transferInDays: number
  transferOutDays: number
  feeVaultAddr: EthAddr
  withdrawControllerAddr: EthAddr
  borrowerVaultAddr: EthAddr
  maxCapacity: ERC20Wei
}

type PoolFlexOverviewCTOInfer = InferType<typeof PoolFlexOverviewCTOSchema>
export const _testAPoolTermOverview: PoolFlexOverviewCTO = {} as unknown as PoolFlexOverviewCTOInfer
export const _testBPoolTermOverview: PoolFlexOverviewCTOInfer = {} as unknown as PoolFlexOverviewCTO
